import { createRouter, createWebHistory, type NavigationGuardNext, type RouteLocationNormalized } from 'vue-router';
import Login from './pages/Login.vue';
import Home from './pages/Home.vue';
import Verify from './pages/Verify.vue';
import Admin from './pages/Admin.vue';
import Onboarding from './pages/Onboarding.vue';
import DraftFirstPreview from './pages/DraftFirstPreview.vue';
import { getAuthMe, ApiClientError } from './api/client';

// Routes that do not require authentication.
const PUBLIC_ROUTES = new Set(['/login']);

const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/login', component: Login },
    { path: '/home', component: Home, meta: { requiresAuth: true } },
    { path: '/verify', component: Verify, meta: { requiresAuth: true } },
    { path: '/admin', component: Admin, meta: { requiresAuth: true, requiresAdmin: true } },
    { path: '/admin/onboarding', component: Onboarding, meta: { requiresAuth: true, requiresAdmin: true } },
    { path: '/design/draft-first', component: DraftFirstPreview },
    { path: '/', redirect: '/home' },
    { path: '/:pathMatch(.*)*', redirect: '/login' },
  ],
});

/**
 * Navigation guard — every requiresAuth route gets gated on /api/auth/me.
 * 401 means no session → redirect to /login. Sign-in is synchronous post
 * ADR-0017/0018: there is no "VP session pending" branch; the cookie is
 * only minted when the OAuth callback has resolved the VP bearer + session.
 */
router.beforeEach(async (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
): Promise<void> => {
  if (PUBLIC_ROUTES.has(to.path)) {
    next();
    return;
  }

  if (!to.meta.requiresAuth) {
    next();
    return;
  }

  try {
    const me = await getAuthMe();

    if (to.meta.requiresAdmin && !(me.superAdmin || me.tenantAdmin)) {
      next({ path: '/home' });
      return;
    }

    next();
  } catch (e) {
    if (e instanceof ApiClientError && e.status === 401) {
      next({ path: '/login', query: { flash: 'Please sign in.' } });
      return;
    }
    // Unexpected errors: allow through; let the page surface them. Don't
    // boot users on transient network failures.
    next();
  }
});

export default router;
